import { useRef, useState } from "react";
import { t } from "i18next";
import { uuid } from "@/utils/string.ts";
import {
  Select,
  SelectItem,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
} from "@edusogno/primitives/select";
import cn from "classnames";

interface AnswerSlotProps {
  type?: "select" | "write";
  number?: string;
  options?: string[];
  readonly?: boolean;
  user_answer?: string;
  correct_answer?: string;
}

export function AnswerSlot({
  type,
  number,
  options,
  readonly,
  user_answer,
  correct_answer,
}: AnswerSlotProps) {
  const ref = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [textActive, setTextActive] = useState(false);

  const validAnswers = correct_answer?.split("/");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClick = () => {
    if (ref.current !== null) {
      ref.current.focus();
      return;
    }

    if (open) {
      handleClose();
      return;
    }

    handleOpen();
  };

  const checkFieldStatus = () => {
    if (type === "select") return answer === null;
    if (answer === null) return true;
    return textActive;
  };

  const convertT = (v: string): string =>
    v
      .trim()
      .toLowerCase()
      .replace(/[.?:,]+$/, "");

  return (
    <button
      onBlur={() => type !== "select" && setTextActive(false)}
      onFocus={() => type !== "select" && setTextActive(true)}
      onClick={() => handleClick()}
      disabled={Boolean(correct_answer)}
      className="w-full flex items-center [&_button]:!py-0 rounded-[10px] min-h-[42px]  min-w-[247px] border border-brand-secondary"
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          setTextActive(false);
        }
      }}
    >
      <div
        className={cn(
          "flex  capitalize items-center flex-[1_1_0]  min-h-[42px] px-4 rounded-[10px]",
          {
            "bg-white": checkFieldStatus(),
            "bg-brand-light": !checkFieldStatus(),
            "bg-[#D3EADD]":
              typeof user_answer !== "undefined" &&
              user_answer?.toLowerCase() === correct_answer?.toLowerCase(),
          },
        )}
      >
        <h2 className="text-[10px] border-[1px] w-[22px] flex items-center justify-center h-[22px] text-center border-solid border-black rounded-[5px] font-semibold text-brand">
          {number}
        </h2>

        <>
          {user_answer && correct_answer ? (
            <div className="flex flex-col px-2 font-medium text-xs w-full items-start ">
              {user_answer !== "N/A" &&
              !validAnswers?.some(
                (e) => convertT(e) === convertT(user_answer),
              ) ? (
                <>
                  <p className="text-[#C80505]">{user_answer}</p>

                  <p className="text-dark">{correct_answer}</p>
                </>
              ) : (
                <p className="text-[#219653]">{correct_answer}</p>
              )}
            </div>
          ) : (
            <>
              {type === "select" ? (
                <Select
                  open={open}
                  value={answer || ""}
                  onOpenChange={(change) => setOpen(change)}
                  onValueChange={(value) => setAnswer(value)}
                >
                  <input ref={ref} type="hidden" value={answer || ""} />

                  <SelectTrigger className="w-full !pl-2 !pr-0 !text-xs !h-[40px] font-semibold !bg-transparent py-0 border-0">
                    <SelectValue placeholder={t("exercises.select")} />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectGroup>
                      {options?.map((option) => (
                        <SelectItem
                          key={uuid()}
                          value={option}
                          className="font-normal text-lg"
                        >
                          {option}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              ) : (
                <input
                  ref={ref}
                  value={answer || ""}
                  readOnly={readonly}
                  onChange={({ target }) => setAnswer(target.value)}
                  className={`w-full text-xs px-2 appearance-none focus:outline-none rounded border ${
                    answer &&
                    correct_answer &&
                    answer.toLowerCase() !== correct_answer.toLowerCase()
                      ? "text-dark"
                      : "text-black"
                  } bg-transparent py-2 text-lg ring-dark-600 placeholder:text-dark-700 focus:border-brand-secondary focus:ring-[0.5px] focus:ring-brand-secondary !border-0 !ring-0`}
                  placeholder={`${t("exercises.write_here")}...`}
                />
              )}
            </>
          )}
        </>
      </div>
    </button>
  );
}

export default AnswerSlot;
